import { useState } from 'react';

import { Popover } from '@mui/material';
import * as yup from 'yup';

import {
  Button,
  ConditionalWrapper,
  Switch,
  notify,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { WorkloadOptimizationState } from '@cast/types';
import { makeValidateSync } from '@cast/utils';

import { useAbility } from '@cast/shared-ability-data-access';
import {
  PreparedWorkload,
  useIsWoopDemoMode,
} from 'features/workload-optimization';
import { useUpdateWorkloadMutation } from 'hooks/mutations/workload-optimization';

import { TypeNotSupportedWrapper } from '../../../_components/TypeNotSupportedWrapper';
import { workloadOverridesToVpaConfigApiModel } from '../../../_utils';
import { useHpaDisabledReasons } from '../../../workload-details/RightsizingSettings/_hooks/useHpaDisabledReasons';

import { RffForm, RffInput } from '@cast/shared-rff-ui';

const validate = makeValidateSync(
  yup.object({
    min: yup
      .number()
      .min(0, 'should be a positive value')
      .required('Required')
      .test((min, context) => {
        const max = context.resolve(yup.ref('max'));
        if (typeof min === 'number' && typeof max === 'number' && min > max) {
          return context.createError({
            message: 'min higher than max value',
          });
        }
        return true;
      }),
    max: yup.number().min(0, 'should be a positive value').required('Required'),
  })
);

type HorizontalRightSizingFormModel = {
  min: number | null;
  max: number | null;
};

type PodCountPopoverProps = {
  workload: PreparedWorkload;
  isOpen: boolean;
  onClose: (reset?: boolean) => void;
  anchorEl: any;
};

const PodCountPopover = ({
  workload,
  isOpen,
  onClose,
  anchorEl,
}: PodCountPopoverProps) => {
  const { mutate } = useUpdateWorkloadMutation();

  const handleSubmit = ({ min, max }: HorizontalRightSizingFormModel) => {
    const vpaConfig = workloadOverridesToVpaConfigApiModel(
      workload.workloadOverrides
    );

    mutate(
      {
        workloadId: workload.id,
        clusterId: workload.clusterId,
        workload: {
          scalingPolicyId: workload.scalingPolicyId,
          workloadConfig: {
            vpaConfig,
            hpaConfig: {
              managementOption: WorkloadOptimizationState.MANAGED,
              minReplicas: min,
              maxReplicas: max,
            },
          },
        },
      },
      {
        onSuccess: () => {
          onClose();
          notify.success(
            `Horizontal optimization enabled saved for ${workload.name} workload`,
            {
              key: 'succeeded-to-enable-hpa',
            }
          );
        },
      }
    );
  };

  return (
    <Popover
      sx={{
        mt: 8,
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isOpen}
      anchorEl={anchorEl}
      onClose={() => onClose(true)}
      hideBackdrop
      disableScrollLock
      onClick={(e) => {
        e.stopPropagation();
      }}
      data-testid="pods-count-popover"
    >
      <RffForm<HorizontalRightSizingFormModel>
        initialValues={{
          min: workload.workloadConfigV2.hpaConfig.minReplicas ?? null,
          max: workload.workloadConfigV2.hpaConfig.maxReplicas ?? null,
        }}
        onSubmit={handleSubmit}
        validate={validate}
      >
        {({ form, valid, dirty }) => {
          return (
            <>
              <Stack gap={8} width={254} p={12}>
                <Typography variant="L10M" color="grey.700">
                  Pod count
                </Typography>
                <Typography variant="P10R" color="textSecondary">
                  Set the desired minimum and maximum replica limits. Further
                  on, the values can be adjusted in the workload’s page
                </Typography>

                <Stack direction="row" gap={8} py={4}>
                  <RffInput
                    name="min"
                    type="number"
                    size="small"
                    min={0}
                    hintAbsolute
                    startAdornment="min:"
                    testId="minPods"
                  />
                  <RffInput
                    name="max"
                    type="number"
                    size="small"
                    min={0}
                    startAdornment="max:"
                    hintAbsolute
                    testId="maxPods"
                  />
                </Stack>
              </Stack>
              <Stack
                direction="row"
                gap={8}
                justifyContent="flex-end"
                px={10}
                py={4}
                borderTop={tokens.theme.border.structural.neutralSubtle}
              >
                <Button
                  variant="text"
                  onClick={() => {
                    form.reset();
                    onClose(true);
                  }}
                  testId="close-button"
                >
                  Close
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={!valid || !dirty}
                  type="submit"
                  testId="apply-button"
                >
                  Apply
                </Button>
              </Stack>
            </>
          );
        }}
      </RffForm>
    </Popover>
  );
};

export const HpaSwitch = (workload: PreparedWorkload) => {
  const canEdit = useAbility().can('update', 'ClusterWorkloadAutoscaler');
  const { isDisabled, typeNotSupported } = useHpaDisabledReasons(workload);
  const isWoopDemoMode = useIsWoopDemoMode();

  const [checked, setChecked] = useState(workload.isHorizontalScalingEnabled);
  const { mutate, isPending } = useUpdateWorkloadMutation();

  const [anchorEl, setAnchorEl] = useState<any>(null);
  const isOpen = !!anchorEl;

  return (
    <>
      <ConditionalWrapper
        condition={typeNotSupported}
        wrapper={(child) => (
          <TypeNotSupportedWrapper>{child}</TypeNotSupportedWrapper>
        )}
      >
        <Switch
          disabled={isWoopDemoMode || !canEdit || isDisabled || isPending}
          testId="hpa-toggle"
          checked={checked}
          size="sm"
          onChange={(e, checked) => {
            setChecked(checked);
            // mutate when disabling, open popover when enabling
            const vpaConfig = workloadOverridesToVpaConfigApiModel(
              workload.workloadOverrides
            );

            if (workload.isHorizontalScalingEnabled) {
              mutate(
                {
                  workloadId: workload.id,
                  clusterId: workload.clusterId,
                  workload: {
                    scalingPolicyId: workload.scalingPolicyId,
                    workloadConfig: {
                      vpaConfig,
                      hpaConfig: {
                        managementOption: WorkloadOptimizationState.READ_ONLY,
                      },
                    },
                  },
                },
                {
                  onSuccess: () =>
                    notify.success(
                      `Horizontal optimization disabled for ${workload.name} workload`,
                      {
                        key: 'succeeded-to-enable-hpa',
                      }
                    ),
                  onError: () => {
                    setChecked(workload.isHorizontalScalingEnabled);
                  },
                }
              );
            } else {
              setAnchorEl(e.target);
            }
          }}
        />
      </ConditionalWrapper>
      <PodCountPopover
        workload={workload}
        isOpen={isOpen}
        onClose={(reset) => {
          if (reset) {
            setChecked(workload.isHorizontalScalingEnabled);
          }
          setAnchorEl(null);
        }}
        anchorEl={anchorEl}
      />
    </>
  );
};
